import type { IMapLayoutFinish } from '@interfaces/map';

import {
  chainEndsInCap,
  chainStartsInLobby,
  findCatalogElement,
  isChainAnchorRole,
  resolveStartElement,
  type MapEditorCatalogElement,
  type MapEditorElementRole,
} from './mapEditorCatalog';
import { parseLayoutFinishes } from './mapEditorFinishes';
import { roomIdsOf, type MapEditorRoom } from './mapEditorRooms';

type SendMessageToUnreal = (message: unknown) => void;

// Rooms carry their resolved geometry, not just an id.
//
// The catalog is a loose JSON file rather than cooked content, so a packaged server has no
// copy to read: it resolved every id to the default gallery and published a straight run of
// identical boxes while the author's own preview — resolved client-side, where the file does
// exist — was correct. Sending the resolution removes the server's dependency on the file.
//
// `id` is still sent and still leads: a server that CAN resolve the catalog keeps doing so,
// and these fields only fill in behind it (UE reads each one only when present). That also
// keeps the payload backward compatible with senders that predate this.
export interface MapLayoutRoom {
  source: 'kit';
  id: string;
  // Which placement of that element this is. UE builds each spot's addressable id from it, so the
  // same module placed twice can hold different content in each copy; without it UE falls back to
  // the room's index, which shifts the moment a room is inserted ahead of it.
  //
  // Optional because two rooms legitimately have none: the cap this hub appends (no spots to
  // address) and any room restored from a layout saved before keys existed.
  roomKey?: string;
  // The pair that replaced the fused `type`. Sent instead of it, never alongside: `type`'s
  // left/right were inverted, and UE reads it only when neither of these is present — so a
  // payload carrying both would be describing the same turn twice, once wrongly.
  family?: string;
  direction?: string;
  size?: string;
  // 'end' seals the chain. Sent explicitly on every room rather than only on the cap, because
  // it is the one field whose default decides whether the rooms after this one are built.
  role?: MapEditorElementRole;
  // Empty string is meaningful, not missing: it is how the catalog marks an element with no
  // room built yet, and UE clears the reference rather than ignoring the field.
  geometryLevel?: string;
  geometryClass?: string;
  // Finishes this room overrides, sent only where it actually differs from the payload's defaults.
  // Omitted rather than sent empty, because the empty array is a different statement — "this room is
  // finished with nothing" — and the room that takes the defaults is the common case.
  finishes?: IMapLayoutFinish[];
}

export interface SpawnLayoutPayload {
  action: 'SPAWN_LAYOUT';
  // Generation of this payload shape. UE reads no meaning from it — it parsed the field into a
  // struct member nothing consumed, and that member is gone — so it is sent purely so a future
  // reader can tell generations apart on a wire that has no other marker. Kept deliberately: the
  // day a shape change needs distinguishing, a payload with no version at all is the harder case.
  version: number;
  clearExisting: boolean;
  rooms: MapLayoutRoom[];
  // Build in the requesting client's world only: nothing replicates, the player is not moved, and no
  // CURRENT_GEOMETRY_LIST is reported. Omitted rather than sent as false for a publish, so the payload
  // stays byte-identical to what backends sent before the flag existed — UE defaults it to false.
  preview?: true;
  // Whether this message leaves a draft on the author's screen. UE hides the published layout while a
  // draft is up, so the author judges what Save will produce rather than their draft stacked on top of
  // what everyone else is standing in.
  //
  // Only ever sent as false, and only on a preview, because it exists to split the one case `preview`
  // cannot express on its own: an empty preview spawn is both "I cleared the list" (a draft of nothing —
  // the published rooms stay hidden, or the author cannot see that saving empties the world) and "I am
  // leaving the editor" (the draft is gone — the published rooms must come back). Everywhere else the
  // value equals `preview`, which is UE's default when the field is absent, so those payloads stay
  // byte-identical to what senders predating this field produced.
  draftActive?: false;
  // Whether UE may close an open chain itself. Defaults to true UE-side, and is left absent
  // everywhere except the one message that must not be closed: the draft teardown.
  //
  // It rides with `draftActive: false` rather than being its own decision, because it answers the
  // same question that flag does. UE closes an empty chain now — the cap seals the map's own
  // entrance, not the author's last room — and on any build that can read the catalog file (the
  // editor and the local loop) it would close this one too, so "the draft is gone" would put a wall
  // on screen instead of taking one down. Every other empty payload wants exactly that closing.
  autoCloseEnd?: false;
  // Finishes every room takes unless its own `finishes` name the same slot. UE lays a room's list
  // over this one by semantic slot, replacing an entry wholesale rather than blending it — an
  // override means "this wall is different", and a blend would produce a surface nobody chose.
  //
  // Named `finishes` here and `finishDefaults` on the saved record deliberately: on the wire this is
  // the layout's own list of finishes and rooms carry theirs under the same name, while a record
  // holding both side by side needs the two told apart. Same rename-at-the-boundary as IBrandingEntity.
  //
  // The layout-wide list carries the load rather than the per-room one, and the reason is arithmetic:
  // a resolved entry is 461 bytes, so a thirty-room chain with four finished slots written per room
  // is 62 KiB where the whole ceiling is 64. See LAYOUT_JSON_BUDGET_BYTES.
  finishes?: IMapLayoutFinish[];
}

export const SPAWN_LAYOUT_VERSION = 1;

// The finishes of a whole layout: what every room takes, and what individual placements override.
//
// Two levels rather than one because that is how authors work — a surface is decided once for the
// whole floor plan ("concrete on every wall"), and a room departs from it as the exception. Written
// the other way round it is also unsendable at scale; see SpawnLayoutPayload.finishDefaults.
export interface MapLayoutFinishSet {
  defaults: IMapLayoutFinish[];
  // Keyed by placement key.
  rooms: Record<string, IMapLayoutFinish[]>;
}

export const EMPTY_FINISH_SET: MapLayoutFinishSet = { defaults: [], rooms: {} };

// The authority's ceiling, from `AMytaMapEditorRelay::MaxLayoutJsonLength`. Over it the spawn is
// refused and what to shorten is logged; the connection survives.
//
// Counted in UTF-16 code units, not bytes — it is compared against `FString::Len()`, and the relay
// measures the whole inbound message rather than a layout-only substring of it, which is the same
// thing measured here. Named in chars for that reason: calling it bytes would assert a unit it does
// not use, and this constant exists to be compared against, so the unit is the whole point.
export const MAX_LAYOUT_JSON_CHARS = 64 * 1024;

// Where this hub stops, under the authority's ceiling by a margin.
//
// Two reasons the margin is real rather than superstition. The measurement here is in UTF-8 bytes
// and the authority's is in UTF-16 code units, and for the same text bytes are never fewer than
// units — equal across ASCII, and two to four times as many above it. So this stop trips at or
// before the authority's, and the difference only ever runs in the safe direction: a non-ASCII
// preset name or room key costs more here than it does there. And the point of stopping first is
// that a refusal raised here can name the rooms to simplify, while the authority's is a log line in
// someone else's process.
export const LAYOUT_JSON_BUDGET_BYTES = 56 * 1024;

/**
 * How many bytes a string is as UTF-8.
 *
 * Counted rather than measured with TextEncoder, which the jsdom the tests run in does not define —
 * and a size guard that cannot be exercised in a test is a size guard nobody trusts. Surrogate pairs
 * are counted once, as the four bytes they encode to.
 *
 * @param value - The string to measure.
 * @returns Its length in bytes.
 */
const utf8Length = (value: string): number => {
  let bytes = 0;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
  }

  return bytes;
};

/**
 * How big a payload is on the wire.
 *
 * Measured in bytes rather than characters, because the limit is a byte length and a name outside
 * ASCII costs more than one byte per character — a Japanese preset name would otherwise measure short.
 *
 * @param payload - The message about to be sent.
 * @returns Its length as UTF-8 bytes.
 */
export const measurePayloadBytes = (payload: unknown): number =>
  utf8Length(JSON.stringify(payload));

/**
 * Whether a publish is small enough to send, and by how much it is not.
 *
 * @param payload - The SPAWN_LAYOUT about to be published.
 * @returns The measurement, the budget it was held to, and how far over it went.
 */
export const layoutJsonBudget = (
  payload: SpawnLayoutPayload,
): { bytes: number; budget: number; overBy: number } => {
  const bytes = measurePayloadBytes(payload);

  return {
    bytes,
    budget: LAYOUT_JSON_BUDGET_BYTES,
    overBy: Math.max(0, bytes - LAYOUT_JSON_BUDGET_BYTES),
  };
};

// An id the catalog does not know still goes on the wire bare. Dropping it would silently
// shorten the layout, and UE already handles an unresolvable room by greyboxing it — a visible
// wrong room an author can see and remove beats a missing one they cannot.
//
// The key is emitted only when the room has one, so a keyless room — the appended cap, or a room
// out of a layout saved before keys existed — produces exactly the payload it did before this field,
// and UE falls back to the index for it.
// A row on its way to the payload: the draft's placement, plus the role the wire row has to carry
// when that is not the role the catalog gives the element.
//
// The entrance is the case, and it is not a detail: opening a chain is a property of the *row*. UE
// takes whatever arrives as `rooms[0]` carrying `role: "start"`, and since any room may be the
// entrance, an ordinary module chosen as one would otherwise go out as `role: "module"` and be built
// as an ordinary first link — no derived arrival point, and the player left at the host map's
// PlayerStart inside the hidden built-in entrance.
type MapEditorPayloadRow = MapEditorRoom & { role?: MapEditorElementRole };

const describeRoom = (
  { id, key, role }: MapEditorPayloadRow,
  elements: MapEditorCatalogElement[],
  roomFinishes: Record<string, IMapLayoutFinish[]> = {},
): MapLayoutRoom => {
  const placement = key ? { roomKey: key } : {};
  // Keyed by placement, so an override follows its room through a reorder the same way its spots do.
  // A keyless room — the appended cap, or one restored from a layout saved before keys existed — can
  // hold no override and takes the payload's defaults.
  const overrides = key ? roomFinishes[key] : undefined;
  const finishes = overrides?.length ? { finishes: overrides } : {};
  const element = elements.find((candidate) => candidate.id === id);
  if (!element) {
    return {
      source: 'kit',
      id,
      ...placement,
      ...(role ? { role } : {}),
      ...finishes,
    };
  }

  return {
    source: 'kit',
    id,
    ...placement,
    family: element.family,
    direction: element.direction,
    size: element.size,
    role: role ?? element.role,
    geometryLevel: element.geometryLevel,
    geometryClass: element.geometryClass,
    ...finishes,
  };
};

// The placement key every lobby stands under.
//
// Fixed rather than minted, and the reason is the same one that makes minted keys right for the
// author's rooms: a key has to be identical across clients and sessions, or the republish each
// participant sends on entry describes a chain UE does not recognise and the world is rebuilt under
// everyone standing in it. A minted key would differ per client; a saved one would still leave the
// first publish of an unchosen lobby to whichever client got there first. There is only ever one
// start row, so a constant is unique by construction.
//
// Not left unkeyed either, which is the trap: UE stamps `i0` on an unkeyed row, and a record whose
// rooms this hub keyed with those same `i<index>` strings (see legacyRoomKey) already holds it — so
// the generated key loses to the named one and the lobby lands on `i0-2`, moving the composite ids
// of every spot in it whenever a legacy-keyed module is added or removed. 'start' is outside both
// the minted `r…` and the fallback `i…` namespaces, so nothing can collide with it.
export const START_ROOM_KEY = 'start';

/**
 * Prepends the lobby the layout opens in as `rooms[0]`.
 *
 * The mirror of appendEndCap, and it has to be resolved here for a stronger reason than the cap
 * does: `bAutoOpenChainStart` is off on the builds this hub talks to, so the runtime supplies no
 * lobby on any path — not just on the streamed one where it cannot read the catalog. If this hub
 * does not send it, no lobby is sent at all.
 *
 * Prepended only when a lobby was actually chosen. An unchosen one is not defaulted in from the
 * catalog, and that is the whole difference between this and the cap: on this kit
 * `defaultStartElement` names the very level the host map already bakes in, so sending it on the
 * author's behalf would hide the map's own entrance and stream an identical copy over the top —
 * and, because the persistence bridge republishes on every entry, would rebuild every existing
 * configuration's world once for no visible change. See the layout's `startElement`.
 *
 * @param rooms - The author's chain, in order.
 * @param elements - The resolved catalog. An id it cannot describe as a lobby is not sent.
 * @param startElementId - The chosen lobby, or empty to open on the map's baked entrance.
 * @returns The chain with the lobby at its head, or unchanged when there is none to add.
 */
const prependStartRoom = (
  rooms: MapEditorPayloadRow[],
  elements: MapEditorCatalogElement[],
  startElementId: string,
): MapEditorPayloadRow[] => {
  const lobbyId = resolveStartElement(startElementId, elements);
  if (!lobbyId) {
    return rooms;
  }

  // A chain already opening on a lobby keeps the one it has. UE refuses a second start row as
  // `start-not-first` — a refusal that reports a bug in this hub rather than anything the author
  // did — and a saved layout can carry one as an ordinary room, from a record written by a sender
  // that folded the lobby into `rooms` instead of holding it beside them. The persistence bridge
  // takes such a row out on the way in (see dropLeadingLobby), so this is the backstop rather than
  // the fix: a chain that reaches here still opening on a lobby is one no reader normalised.
  if (chainStartsInLobby(roomIdsOf(rooms), elements)) {
    return rooms;
  }

  // The role is stated on the row rather than inherited from the element, because for every room but
  // the two purpose-built shells the catalog says 'module' — see MapEditorPayloadRow.
  return [{ id: lobbyId, key: START_ROOM_KEY, role: 'start' }, ...rooms];
};

/**
 * Appends the cap that closes the chain as its last room.
 *
 * UE closes an open chain on its own — but only where it can read the catalog to resolve
 * `defaultEndElement`, which a packaged server cannot. On the streamed path, the path that matters,
 * the cap therefore has to be resolved and carried here like every other room's geometry, or a
 * published layout ends in an open doorway leading into nothing.
 *
 * An empty chain is capped too, which reads as an exception and is not one. The chain does not
 * begin at its first room: it begins at the doorway of the entrance module the map itself stands
 * up — UE resolves it in ResolveChainStart, the LinearMuseum lobby is one — and that module is
 * there whether or not anybody ever published a layout. So "no rooms" does not mean "no geometry".
 * It means the entrance with its exit hanging open, and the first author to walk into a fresh
 * configuration walks out of that doorway and falls. A lone cap seals exactly it.
 *
 * Nothing is invented: with no cap in the catalog the chain is left open, which is a valid layout,
 * and an empty chain with no cap to resolve stays the empty payload it was — that one really is
 * the declarative spelling of a clear.
 *
 * The cap is deliberately keyless, where the lobby is keyed. It has no spots, so there is nothing
 * in it to address, and minting a key for a room the author never picked would put an entry in the
 * saved layout for something that is appended at publish time. See START_ROOM_KEY for why the
 * lobby cannot be treated the same way.
 *
 * @param rooms - The chain to close, lobby already at its head.
 * @param elements - The resolved catalog. An id it cannot describe as a cap is not sent.
 * @param endElementId - The kit's `defaultEndElement`, or empty to leave the chain open.
 * @returns The chain with the cap at its end, or unchanged when there is none to add.
 */
const appendEndCap = (
  rooms: MapEditorRoom[],
  elements: MapEditorCatalogElement[],
  endElementId: string,
): MapEditorRoom[] => {
  if (!endElementId) {
    return rooms;
  }

  const cap = findCatalogElement(elements, endElementId);
  if (cap?.role !== 'end') {
    return rooms;
  }

  // A chain already ending in a cap is closed. UE drops rooms after an End module, so a second
  // one would be refused as 'chain-closed' — a refusal for a room the author never picked.
  if (chainEndsInCap(roomIdsOf(rooms), elements)) {
    return rooms;
  }

  return [...rooms, { id: endElementId, key: '' }];
};

export const buildSpawnLayoutPayload = (
  rooms: MapEditorRoom[],
  preview = false,
  elements: MapEditorCatalogElement[] = [],
  draftActive = preview,
  endElementId = '',
  startElementId = '',
  finishes: MapLayoutFinishSet = EMPTY_FINISH_SET,
): SpawnLayoutPayload => ({
  action: 'SPAWN_LAYOUT',
  version: SPAWN_LAYOUT_VERSION,
  clearExisting: true,
  // Capped on the preview too, not only on the publish: the preview exists so the author
  // judges what Save will produce, and a draft missing the wall the publish adds is not that.
  //
  // The lobby goes on first and the cap last, in that order, because the cap's own rule reads the
  // chain it is closing: a layout of nothing but a lobby is `[lobby, cap]`, and the cap seals the
  // lobby's exit rather than the baked entrance's. Appending the cap first would leave
  // chainEndsInCap answering about a chain the lobby had not been added to yet.
  rooms: appendEndCap(
    prependStartRoom(rooms, elements, startElementId),
    elements,
    endElementId,
  ).map((room) => describeRoom(room, elements, finishes.rooms)),
  ...(finishes.defaults.length ? { finishes: finishes.defaults } : {}),
  ...(preview ? { preview: true as const } : {}),
  // Emitted only where it disagrees with the default UE would infer, so every payload that was correct
  // before this field existed still goes out unchanged.
  //
  // The two travel together because they describe one message. A preview that leaves no draft on
  // screen is the teardown, and the teardown is the only spawn this hub sends that must come back
  // with nothing built — including the cap UE would otherwise seal its empty chain with.
  ...(preview && !draftActive
    ? { draftActive: false as const, autoCloseEnd: false as const }
    : {}),
});

// Deliberately not gated on a non-empty chain. An empty list is still a layout —
// "nothing" — and clearExisting with no rooms is the declarative spelling of a clear,
// so sending it reproduces what the author sees. Refusing to send on empty is what
// broke the prototype's 1:1 guarantee: the button became a no-op while the previously
// built layout stayed standing in UE, so an author who emptied the list watched the
// old rooms remain.
export const spawnLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  rooms: MapEditorRoom[],
  elements: MapEditorCatalogElement[] = [],
  endElementId = '',
  startElementId = '',
  finishes: MapLayoutFinishSet = EMPTY_FINISH_SET,
) => {
  sendMessageToUnreal(
    buildSpawnLayoutPayload(
      rooms,
      /*preview=*/ false,
      elements,
      /*draftActive=*/ false,
      endElementId,
      startElementId,
      finishes,
    ),
  );
};

// The draft made visible. Sent on every edit so the author sees the floor plan they are assembling —
// UE builds these rooms in their client's world alone, so no other participant is affected and the
// player is not teleported into them.
//
// Carries the same keys the publish will. The draft has to address the same spots as the publish, or
// an author brands a preview and the content lands nowhere.
export const previewLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  rooms: MapEditorRoom[],
  elements: MapEditorCatalogElement[] = [],
  endElementId = '',
  startElementId = '',
  finishes: MapLayoutFinishSet = EMPTY_FINISH_SET,
) => {
  sendMessageToUnreal(
    buildSpawnLayoutPayload(
      rooms,
      /*preview=*/ true,
      elements,
      /*draftActive=*/ true,
      endElementId,
      startElementId,
      finishes,
    ),
  );
};

export const clearLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
) => {
  sendMessageToUnreal({ action: 'CLEAR_LAYOUT' });
};

// Tears down the local draft geometry without touching the published layout. Deliberately an empty
// preview spawn rather than a CLEAR_PREVIEW action: clearExisting on a preview already clears the
// preview UE-side, so "a draft of nothing" expresses this exactly, and CLEAR_LAYOUT would be wrong —
// that one is authoritative and would take the published layout down with it.
//
// draftActive:false is the part that makes it a teardown rather than an empty draft. Without it UE read
// this message as "the author cleared their list" and kept the published layout hidden — and because the
// flag lives on the GameInstance, it survived into the next session: a user entering a map with rooms
// already standing saw an empty world until someone published.
//
// Both halves of "this one is not capped" are load-bearing rather than omissions, because an empty
// chain is capped like any other now (see appendEndCap): passing no catalog and no cap id keeps this
// hub from appending one, and the `autoCloseEnd: false` the builder pairs with `draftActive: false`
// keeps UE from appending one on a build that can read the catalog itself. A teardown that carried a
// cap either way would put the draft's wall on screen in front of the published layout it is handing
// back.
//
// The same omission carries the lobby, and there it is the whole difference between the two shapes
// of empty this message could be confused with: `rooms: []` is the declarative clear that puts the
// map's baked entrance back, while `[lobby]` is an empty *layout* that leaves a lobby standing. A
// teardown means the first one, so it names no start element and passes no catalog to resolve one
// from.
export const clearPreviewLayoutInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
) => {
  sendMessageToUnreal(
    buildSpawnLayoutPayload([], /*preview=*/ true, [], /*draftActive=*/ false),
  );
};

export type MapEditorUnrealView = 'plan' | 'walk';

// `enabled` is optional on the wire and defaults to true UE-side, but it is always sent
// explicitly here: the sender knows the state it wants, and a toggle would desync if a
// message were retried or dropped.
//
// EDITOR_MODE was once named PREVIEW_LAYOUT, and UE accepted both for a while. That alias
// has been removed: the only sender is this bundle, which every client loads fresh, so there
// was no pinned pre-rename sender left to serve — unlike a UE build, which is tied to an event
// and lives for months. Sending the old name now routes nowhere.
/**
 * Puts the author in or out of editor mode, and chooses which view of it they are in.
 *
 * @param sendMessageToUnreal - The socket.
 * @param enabled - Whether to be in editor mode at all. This is the field that decides: `enabled:
 * false` leaves, whatever `view` says, because leaving is the reading that message has always had.
 * @param view - `plan` for the overhead view, `walk` to stand in the layout on foot.
 * @param roomKey - Which segment to begin the walk in. Only meaningful with `view: 'walk'`, and only
 * sent then: it is what **Take me there** puts on the wire, and it is the placement key the segment
 * stands under. Omitted — or naming a segment that is not standing — starts the walk at the map's own
 * player start, which is what every walk did before this field existed. A build older than the field
 * ignores it and does exactly that, so the control degrades to "walk the layout" rather than failing.
 */
export const setEditorModeInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  enabled: boolean,
  view: MapEditorUnrealView = 'plan',
  roomKey = '',
) => {
  sendMessageToUnreal({
    action: 'EDITOR_MODE',
    enabled,
    ...(enabled ? { view } : {}),
    ...(enabled && view === 'walk' && roomKey ? { roomKey } : {}),
  });
};

export type MapEditorViewState = 'off' | MapEditorUnrealView;

// The refusal codes this hub has a phrase for.
//
// A code rather than a sentence, on the same rule MapLayoutRefusalReason follows. Unlike that one,
// though, the **field** on the wire is typed as a plain string rather than as this union: a code
// outside this set is still a failure, and carrying it through is what lets a build ship a fifth
// reason without waiting for a hub release. `describeLandFailure` matches these four and falls into
// a default phrase for anything else — see there for why the fallback says less rather than guessing.
//
// Each one names something UE **measured**, which is the property to preserve if this list ever
// grows again:
//
//  - 'no-such-segment' — this side's fault: the key named nothing standing, so the tree is stale.
//  - 'no-floor' — a downward trace at the landing point hit nothing. The module's geometry does not
//    match its declared extent, or its level has not streamed in yet. The only one of the four that
//    points at something fixable.
//  - 'blocked' — there was a floor, and the pawn did not move: every candidate spot around the point
//    was rejected as occupied. Crowded rather than broken, so retrying is meaningful.
//  - 'not-arrived' — the pawn moved and still is not in that segment. UE does not know why (a server
//    correction, or a point that fell inside a neighbour's footprint), and says so rather than
//    naming a cause it did not measure.
//
// 'not-navigable' was the whole vocabulary before these and is **gone**: it was reported for any
// mismatch on the assumption that a safe teleport had been refused by the navmesh, and the teleport
// path never queries navigation at all. It is the reason a day of this investigation went into
// navmeshes. A code that names an unverified cause is worse than one that admits ignorance, which is
// what 'not-arrived' is for.
export type MapEditorKnownLandFailure =
  | 'no-such-segment'
  | 'no-floor'
  | 'blocked'
  | 'not-arrived';

export interface MapEditorModeState {
  enabled: boolean;
  view: MapEditorViewState;
  draftActive: boolean;
  roomCount: number;
  canWalk: boolean;
  // Which segment the author is standing in, by placement key. Three states, and all three are
  // different answers rather than one answer with two spellings:
  //
  //  - **absent** — the question does not apply: the view is not the walk, or the build predates the
  //    field. Read as "unknown", never as "nowhere".
  //  - **empty string** — walking, and inside no segment: between modules, past the end of the
  //    chain, or in the gap a refused segment left. Observed live while a draft was being rebuilt.
  //  - **a key** — standing in that placement.
  //
  // Optional on purpose, so `'currentRoomKey' in state` distinguishes the first from the second.
  currentRoomKey?: string;
  // Whether the segment the walk was asked to begin in is the one the author ended up in. Three
  // states again, and the absent one is doing more work here than it looks:
  //
  //  - **absent** — no landing was asked for, **or one is still in flight**. UE lands through a
  //    ground poll, so at the moment it answers a request the outcome is honestly unknown; a
  //    premature `false` would tell the author a standing segment is unreachable. The sender can
  //    tell the two apart because it knows whether it just asked — a reader that did not ask
  //    cannot, and that is a known limit of this shape rather than an oversight.
  //  - **true** — asked, and the author is there.
  //  - **false** — asked, and they are not. `landFailure` says why.
  landed?: boolean;
  // Only ever present alongside `landed: false`: a success has no reason, and an empty string would
  // render as an empty sentence.
  landFailure?: string;
}

const EDITOR_MODE_VIEWS: MapEditorViewState[] = ['off', 'plan', 'walk'];

/**
 * Reads an EDITOR_MODE_STATE payload.
 *
 * Defensive like the rest of this module, and with one rule of its own: `enabled` decides, exactly
 * as it does on the way out. A payload that is in the editor but names no view this hub knows is
 * read as the plan view rather than dropped — UE resolves an unknown view the same way, and a
 * control that refused would strand the author with no way back.
 *
 * @param payload - The message's `payload` object, straight off the socket.
 * @returns The state, or null when the payload is not one — which callers keep the last known state
 * for, since "unparseable" is not "the author left the editor".
 */
export const parseEditorModeState = (
  payload: Record<string, unknown> | null | undefined,
): MapEditorModeState | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const state = payload as {
    enabled?: unknown;
    view?: unknown;
    draftActive?: unknown;
    roomCount?: unknown;
    canWalk?: unknown;
    currentRoomKey?: unknown;
    landed?: unknown;
    landFailure?: unknown;
  };

  const enabled = state.enabled === true;
  const named = EDITOR_MODE_VIEWS.includes(state.view as MapEditorViewState)
    ? (state.view as MapEditorViewState)
    : 'plan';

  // Carried forward only when the wire carried them, and that is the whole point: for these three
  // "absent" is a state of its own, so the usual `=== true` / `?? 0` normalisation the fields above
  // get would erase the distinction. An absent `landed` read as `false` would turn "still landing"
  // into "cannot land"; an absent `currentRoomKey` read as `''` would turn "not walking" into
  // "walking nowhere".
  //
  // A wrong *type* is dropped rather than coerced, on the same rule: a build sending `landed: "yes"`
  // has told us nothing we can act on, and treating it as truthy would invent a verdict.
  const optional: Partial<MapEditorModeState> = {};
  if (typeof state.currentRoomKey === 'string') {
    optional.currentRoomKey = state.currentRoomKey;
  }
  if (typeof state.landed === 'boolean') {
    optional.landed = state.landed;
  }
  // Only beside a failure, and only non-empty. A code sent with `landed: true` is a contradiction on
  // the wire; keeping it would let a consumer render a reason for a landing that worked.
  if (
    optional.landed === false &&
    typeof state.landFailure === 'string' &&
    state.landFailure
  ) {
    optional.landFailure = state.landFailure;
  }

  return {
    enabled,
    view: enabled ? (named === 'off' ? 'plan' : named) : 'off',
    draftActive: state.draftActive === true,
    roomCount: typeof state.roomCount === 'number' ? state.roomCount : 0,
    canWalk: state.canWalk === true,
    ...optional,
  };
};

// UE's editor mode is the overhead plan view: it spawns a camera, takes over the view
// target and blocks player input. That is right for building a floor plan and wrong for
// every other focus — spots, hotspots and AI scripts are all edited by looking at and
// clicking things in-world, which an off-screen pawn with blocked input cannot do.
//
// So it tracks the map-editor focus rather than the editor overlay as a whole. The
// feature gate is mirrored from the Build/Clear buttons: with SPAWN_LAYOUT off there is
// no layout to look at, so seizing the camera would strand the player for nothing.
export const shouldUnrealEditorModeBeOn = (
  activeFocus: string,
  canUseMapEditor: boolean,
): boolean => activeFocus === 'mapEditor' && canUseMapEditor;

// Why the authority would not place a room that was asked for.
//
// 'overlap' is the room that landed on one already standing; 'chain-truncated' is every room after
// it, which was never attempted — UE stops walking the chain at the first refusal rather than
// scattering the remainder around the blockage. 'chain-closed' is a room that came after an End
// module: a cap seals the chain, and there is no doorway left to attach to.
//
// 'start-not-first' is the odd one out: a lobby that arrived anywhere but at index 0, or a second
// one. The other three describe a layout the author assembled, and each names something they can
// go and change; this one can only be produced by a sender that built the payload wrongly, so it
// reports a bug in this hub and there is nothing in the room list to point at.
export type MapLayoutRefusalReason =
  | 'overlap'
  | 'chain-truncated'
  | 'chain-closed'
  | 'start-not-first';

export interface MapLayoutRefusal {
  // Position in the chain that was published, not an instance id: a refused room was never spawned
  // and has none. The fallback identity, for a report that carries no key.
  index: number;
  // Placement key of the refused row — the field to match on wherever it is present. An index is only
  // valid against the exact chain that produced the report, so it goes stale the moment the author
  // inserts or moves a row; a key travels with its placement and stays right through any rearranging.
  // Absent from a report older than the field, and from the runtime's own end cap.
  roomKey?: string;
  id: string;
  reason: MapLayoutRefusalReason;
  // Only an overlap names a culprit. UE sends `blockedBy: { instanceId, id }`; the instance id is
  // dropped here because the web tracks a chain of ids and has nothing to match one against.
  //
  // Named in the segment panel, which is where it earns its keep: the panel is about the refused
  // segment already, so the useful half of the sentence is what it ran into. "Runs into Corridor"
  // tells the author where to look; "runs into a segment already standing" leaves them counting rows.
  blockedById?: string;
  // The refused room was UE's own end cap rather than one this author picked, so its index points
  // past the chain they wrote. Nothing in the room list can be marked for it — the inspector says
  // so on its own row instead.
  auto?: boolean;
}

const REFUSAL_REASONS: MapLayoutRefusalReason[] = [
  'overlap',
  'chain-truncated',
  'chain-closed',
  'start-not-first',
];

/**
 * Reads the `refused` half of a CURRENT_GEOMETRY_LIST payload.
 *
 * Defensive for the same reason the rooms half is: this crosses UE → WebSocket → here, and a
 * malformed entry must cost its own row rather than the whole report. An unknown reason is dropped
 * rather than passed through — every consumer switches on it to decide what to tell the author, and
 * a reason nothing can explain would render as a warning with no text.
 *
 * @param payload - The message's `payload` object, straight off the socket.
 * @returns The refusals in wire order; empty when the field is absent, which is the normal case.
 */
export const parseGeometryRefusals = (
  payload: Record<string, unknown> | null | undefined,
): MapLayoutRefusal[] => {
  const raw = (payload as { refused?: unknown } | null | undefined)?.refused;
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.reduce<MapLayoutRefusal[]>((refusals, entry) => {
    const candidate = entry as Partial<MapLayoutRefusal> & {
      blockedBy?: { id?: unknown };
    };

    if (
      typeof candidate?.index !== 'number' ||
      typeof candidate?.id !== 'string' ||
      !candidate.id ||
      !REFUSAL_REASONS.includes(candidate.reason as MapLayoutRefusalReason)
    ) {
      return refusals;
    }

    const blockedById = candidate.blockedBy?.id;

    refusals.push({
      index: candidate.index,
      // Empty is dropped rather than carried: an empty key matches nothing, and leaving the field off
      // is what makes the index fallback in findRefusalFor reachable.
      ...(typeof candidate.roomKey === 'string' && candidate.roomKey
        ? { roomKey: candidate.roomKey }
        : {}),
      id: candidate.id,
      reason: candidate.reason as MapLayoutRefusalReason,
      ...(typeof blockedById === 'string' && blockedById
        ? { blockedById }
        : {}),
      ...(candidate.auto === true ? { auto: true } : {}),
    });

    return refusals;
  }, []);
};

// One brandable spot UE found inside a standing room's level.
export interface MapLayoutSpot {
  // The addressable id, and the only field anything may key on. Composed by UE from the room's key
  // and the spot's position within the module, and opaque here: it is read out of this message and
  // passed through, never constructed, parsed or re-derived. Empty for a room that arrived with no
  // key, which is how a reader tells a spot it cannot address individually from one it can.
  spotId: string;
  // The id authored into the level asset. Diagnostics only — it is the field that exposes a kit
  // whose spots all share one id, and keying anything on it is exactly the bug spotId fixes.
  authoredId: string;
  type: string;
  // How big the frame is, from UE cs:15082 on. **Unreal units, not pixels**, despite the names —
  // the same `ComputeDesignWidthHeightFromMeshes` extents the in-world click path reports, so a
  // size read here and one read off a click agree exactly. Only the *aspect* and the relative size
  // between frames are safe to build on; the absolute number is pixels only where the art happens
  // to be drawn at one unit per pixel.
  //
  // Present as a **pair or not at all**: a frame UE could not measure reports no size rather than
  // zero, so half a pair is no size. Absent from any build older than the field, which is not the
  // same as a frame measured at nothing.
  width?: number;
  height?: number;
}

export interface MapLayoutRegion {
  regionName: string;
  authoredName: string;
  spotIds: string[];
}

// A room the authority reports as standing.
export interface MapLayoutStandingRoom {
  id: string;
  // The key this placement is standing under: the one this hub sent, or the index fallback UE
  // stamped for a room that arrived without one. Empty only from a build older than the field.
  roomKey: string;
  // Absent means the room's level has not been read — it is still streaming, or the room has no
  // level at all — while an empty array is the real answer for a module with no spots authored, and
  // one shipped room genuinely has zero. The same distinction the build metadata makes for
  // IMapLayoutElementSpots, and it has to survive the parse for the UI to be able to say which is
  // which.
  spots?: MapLayoutSpot[];
  regions?: MapLayoutRegion[];
  // Semantic slots this room's shell offers. Read for verification rather than to build the menu:
  // the offer has to be answerable for a room in an unpublished draft, and a preview spawn is never
  // reported, so the menu resolves slots from the catalog's slot mapping instead.
  finishSlots?: string[];
  // Finishes as actually applied, after the authority clamped them. The clamped value is the point:
  // an author whose relief steps were cut has to see the cut number rather than the one they asked
  // for. Absent from a build older than the field, which is not the same as a room with no finishes.
  finishes?: IMapLayoutFinish[];
}

/**
 * Whether a reported extent is a measurement.
 *
 * A number and only a number: the frame's size travels as a JSON number here, where the older
 * `CUSTOMISATION_ITEM_SELECTED` path encodes the same two values as strings. Coercing a string
 * would make this reader accept the shape UE deliberately moved away from, and the two paths would
 * then disagree about which one is the contract.
 *
 * @param value - One of the pair, straight off the socket.
 * @returns True when it is a positive, finite number.
 */
const isMeasuredExtent = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Reads one spot entry of a standing room.
 *
 * @param entry - Element of the room's `spots` array, straight off the socket.
 * @returns The spot, or null when the entry carries nothing usable.
 */
const parseSpot = (entry: unknown): MapLayoutSpot | null => {
  const spot = entry as Partial<MapLayoutSpot> | null;
  if (typeof spot?.spotId !== 'string') {
    return null;
  }

  // Pair or nothing, so a frame with one usable extent is read as unmeasured rather than as half a
  // size. Set only when both are there, so a build older than the field leaves the keys off
  // entirely — absent and "measured at zero" have to stay distinguishable.
  const hasSize = isMeasuredExtent(spot.width) && isMeasuredExtent(spot.height);

  return {
    spotId: spot.spotId,
    authoredId: typeof spot.authoredId === 'string' ? spot.authoredId : '',
    type: typeof spot.type === 'string' ? spot.type : '',
    ...(hasSize ? { width: spot.width, height: spot.height } : {}),
  };
};

/**
 * Reads one AI region entry of a standing room.
 *
 * A nameless entry is dropped rather than carried, where a spot with an empty id is kept: an empty
 * spot id still describes a spot that is standing and merely cannot be addressed alone, while the
 * name *is* a region's whole address — every consumer keys on it, and an unnamed one would take the
 * empty string as a trigger name. UE stamps a positional ordinal onto a region whose asset left the
 * field blank, so an entry arriving without one is a malformed message rather than a real region.
 *
 * @param entry - Element of the room's `regions` array, straight off the socket.
 * @returns The region, or null when the entry carries nothing usable.
 */
const parseRegion = (entry: unknown): MapLayoutRegion | null => {
  const region = entry as
    | (Partial<MapLayoutRegion> & { spots?: unknown })
    | null;
  if (typeof region?.regionName !== 'string' || !region.regionName) {
    return null;
  }

  return {
    regionName: region.regionName,
    authoredName:
      typeof region.authoredName === 'string' ? region.authoredName : '',
    spotIds: Array.isArray(region.spots)
      ? region.spots.filter(
          (id): id is string => typeof id === 'string' && !!id,
        )
      : [],
  };
};

// One entry of a report's `rooms`, before anything has been decided about it. The union is what the
// wire actually carries: an object since rooms grew fields, and a bare string before that.
type GeometryRoomEntry =
  | string
  | {
      id?: unknown;
      roomKey?: unknown;
      role?: unknown;
      auto?: unknown;
      spots?: unknown;
      regions?: unknown;
      finishSlots?: unknown;
      finishes?: unknown;
    }
  | null;

/**
 * The `rooms` array of a report, or nothing when the field is absent or malformed.
 *
 * Shared by the two readers of the same field so the cast and the array guard are written once —
 * they have to agree about what a report even is, or one of them silently answers for a payload the
 * other rejected.
 *
 * @param payload - The message's `payload` object, straight off the socket.
 * @returns The entries in report order; empty when there is no usable array.
 */
const geometryRoomEntries = (
  payload: Record<string, unknown> | null | undefined,
): GeometryRoomEntry[] => {
  const raw = (payload as { rooms?: unknown } | null | undefined)?.rooms;

  return Array.isArray(raw) ? (raw as GeometryRoomEntry[]) : [];
};

/**
 * Whether a report entry is an anchor rather than one of the author's rooms.
 *
 * True for a row UE appended itself (`auto: true`) and for either anchor role, which is how the
 * rows this hub sent come back — `auto: false`, indistinguishable from a picked room except by the
 * role. Asked through CHAIN_ANCHOR_ROLES rather than against the two literals, so a third anchor
 * role cannot be folded into the author's chain by being forgotten here.
 *
 * @param entry - One entry of the report's `rooms`.
 * @returns True when the entry belongs to a position in the chain rather than a link in it.
 */
const isAnchorEntry = (entry: GeometryRoomEntry): boolean =>
  typeof entry !== 'string' &&
  !!entry &&
  (entry.auto === true || isChainAnchorRole(entry.role));

/**
 * Reads one standing room out of a report entry.
 *
 * @param entry - One entry of the report's `rooms`. Tolerant of the bare string form.
 * @returns The room, or null when the entry carries no usable id.
 */
const parseStandingRoom = (
  entry: GeometryRoomEntry,
): MapLayoutStandingRoom | null => {
  if (typeof entry === 'string') {
    return entry ? { id: entry, roomKey: '' } : null;
  }

  const room = entry;
  if (typeof room?.id !== 'string' || !room.id) {
    return null;
  }

  // Trimmed for the same reason UE trims it on the way in: the key becomes part of a spot id that
  // is matched literally, and a stray space would break the match invisibly.
  const roomKey = typeof room.roomKey === 'string' ? room.roomKey.trim() : '';

  // Absence is carried through as absence — the field is set only when the array is there, so a
  // room whose level has not been read stays distinguishable from one that holds no spots. An
  // empty array is kept, since that is the real answer for a module with no spots authored.
  const spots = Array.isArray(room.spots)
    ? room.spots.reduce<MapLayoutSpot[]>((parsed, spotEntry) => {
        const spot = parseSpot(spotEntry);
        return spot ? [...parsed, spot] : parsed;
      }, [])
    : undefined;

  const regions = Array.isArray(room.regions)
    ? room.regions.reduce<MapLayoutRegion[]>((parsed, regionEntry) => {
        const region = parseRegion(regionEntry);
        return region ? [...parsed, region] : parsed;
      }, [])
    : undefined;

  // Absence carried through as absence, for the same reason it is for spots: a build older than
  // the field says nothing about finishes, which is not the same as reporting a room with none.
  const finishSlots = Array.isArray(room.finishSlots)
    ? room.finishSlots
        .filter((slot): slot is string => typeof slot === 'string' && !!slot)
        .map((slot) => slot.trim().toLowerCase())
    : undefined;
  const finishes = parseLayoutFinishes(room.finishes);

  return {
    id: room.id,
    roomKey,
    ...(spots ? { spots } : {}),
    ...(regions ? { regions } : {}),
    ...(finishSlots ? { finishSlots } : {}),
    ...(finishes ? { finishes } : {}),
  };
};

/**
 * Reads the `rooms` half of a CURRENT_GEOMETRY_LIST payload as the author's own room list.
 *
 * Both anchors are dropped, and each arrives two ways: appended or prepended by UE (`auto: true`)
 * and sent by this hub itself, which comes back with the anchor role and `auto: false`. Neither is
 * a room the author picked. Folding a cap in would have them adopt it as their own and the next
 * publish would append a second one behind it — two walls where the author asked for one; folding a
 * lobby in would have them adopt it as the first link of their chain and the next publish would
 * prepend a second one that UE refuses as `start-not-first`.
 *
 * The dropped rows are not discarded, only routed: parseGeometryStartRoom reads the lobby's own row
 * back out of the same payload, so the entrance's regions and spots stay available to the surfaces
 * that address them. The cap has neither, which is why nothing reads it.
 *
 * Tolerant of a bare string entry, which is what the report carried before rooms grew fields.
 *
 * @param payload - The message's `payload` object, straight off the socket.
 * @returns The standing rooms in chain order; empty when the field is absent or malformed.
 */
export const parseGeometryRooms = (
  payload: Record<string, unknown> | null | undefined,
): MapLayoutStandingRoom[] =>
  geometryRoomEntries(payload).reduce<MapLayoutStandingRoom[]>(
    (rooms, entry) => {
      if (isAnchorEntry(entry)) {
        return rooms;
      }

      const room = parseStandingRoom(entry);

      return room ? [...rooms, room] : rooms;
    },
    [],
  );

/**
 * Reads the lobby out of a CURRENT_GEOMETRY_LIST payload — the room the standing layout opens in.
 *
 * The other half of the drop parseGeometryRooms performs: that one takes the start row out of the
 * author's chain, and this one hands it to the surfaces that own it. Without it a standing lobby is
 * invisible to the hub, and the author is shown the kit's default while the world opens on
 * something else.
 *
 * Returns the whole row rather than its id, because the id is the least of what it carries. The cap
 * can be dropped and forgotten — it has no spots and no regions — while the shipped lobby has eight
 * spots and its own AI regions, and those reach the hub through this message alone: build metadata
 * describes an element, not a placement, so a region inside the entrance has no other source. The
 * id answers the lobby control; the regions answer the AI script tree.
 *
 * A row UE prepended itself is not one, and it marks such a row `auto: true` exactly as it marks an
 * auto-appended cap. Adopting it would seed the draft with a lobby nobody picked and the next Save
 * would persist the kit default — the one thing prependStartRoom exists never to send.
 *
 * `bAutoOpenChainStart` ships off, and `ShouldAutoOpenChainStart()` was made fail-*closed* once this
 * check existed to compare it against — it had been copied in shape from the auto-close gate, where
 * failing open is right because an open doorway is never wanted. So this guard is now a belt to that
 * braces: what it still covers is a client talking to an older binary, where the gate fails open and
 * the runtime hides the map's own lobby behind an identical streamed copy of it.
 *
 * Read by role rather than by position. A start row is only ever `rooms[0]` in a well-formed report
 * — UE refuses one anywhere else — but a refused row is dropped from the report entirely, so
 * position is not evidence of anything here; the role is what UE actually decided on.
 *
 * @param payload - The message's `payload` object, straight off the socket.
 * @returns The standing lobby, or null when the layout opens on the entrance the host map bakes in
 * — which is every layout that has never named one.
 */
export const parseGeometryStartRoom = (
  payload: Record<string, unknown> | null | undefined,
): MapLayoutStandingRoom | null => {
  const lobby = geometryRoomEntries(payload).find(
    (entry) =>
      typeof entry !== 'string' &&
      !!entry &&
      entry.role === 'start' &&
      entry.auto !== true,
  );

  return lobby ? parseStandingRoom(lobby) : null;
};

/**
 * The spots of the standing room a draft row addresses.
 *
 * Matched on the placement key rather than on the row's position, which is what the key is for: the
 * report drops refused rooms, so positions stop lining up exactly when a layout is interesting.
 * A row with no key matches nothing — an empty key is shared by every unkeyed room, so matching on
 * it would attribute one room's spots to another.
 *
 * @param builtRooms - Standing rooms from the last report, or null before one has arrived.
 * @param roomKey - The draft row's key.
 * @returns Its spots, or undefined when the row is not standing or its level has not been read —
 * which is not the same answer as an empty array.
 */
export const findStandingSpots = (
  builtRooms: MapLayoutStandingRoom[] | null,
  roomKey: string,
): MapLayoutSpot[] | undefined => {
  if (!roomKey) {
    return undefined;
  }

  return builtRooms?.find((room) => room.roomKey === roomKey)?.spots;
};

/**
 * The AI regions of every standing room, flattened.
 *
 * Flat rather than per-room because the only consumer is the region index, which answers "is this a
 * trigger name that exists" and "which region owns this spot" — neither of them a question about one
 * room.
 *
 * Deliberately not deduplicated by name. A stamped name is unique to its placement, so the only way
 * two entries share one is a room standing without a placement key, which keeps its authored names;
 * merging those means deciding what to do with two spot lists, and that decision belongs where the
 * runtime regions meet the build metadata's, not here.
 *
 * @param builtRooms - Standing rooms from the last report, or null before one has arrived.
 * @returns The regions in report order; empty before a report, and while no level has been read.
 */
export const collectStandingRegions = (
  builtRooms: MapLayoutStandingRoom[] | null | undefined,
): MapLayoutRegion[] => builtRooms?.flatMap((room) => room.regions ?? []) ?? [];

/**
 * Finds the refusal that explains a given row of the chain.
 *
 * By placement key first, and only by index where the report carries none. That order is what lets a
 * marker stay on the right row after an edit: a key belongs to the placement, so it follows the row
 * however the list is rearranged, while an index is only meaningful against the chain that produced
 * the report. Neither is matched on element id — the same element can appear several times in a chain
 * and only one of those placements may have been refused.
 *
 * @param refusals - Refusals from the last report, or null before one has arrived.
 * @param key - Placement key of the row being rendered. Empty for a row that has none.
 * @param index - Position of that row, used only as the fallback.
 * @returns The refusal for that row, or undefined when it was built.
 */
export const findRefusalFor = (
  refusals: MapLayoutRefusal[] | null,
  key: string,
  index: number,
): MapLayoutRefusal | undefined => {
  if (!refusals?.length) {
    return undefined;
  }

  if (key) {
    const keyed = refusals.find((refusal) => refusal.roomKey === key);
    if (keyed) {
      return keyed;
    }

    // A keyed report that does not mention this row means the row was built. Falling through to the
    // index would then answer with whatever refusal happens to sit at that position — a different
    // row's — which is the failure mode the key exists to remove.
    if (refusals.some((refusal) => !!refusal.roomKey)) {
      return undefined;
    }
  }

  return refusals.find((refusal) => refusal.index === index);
};
